import { Form, Input } from "antd";
import { ReactElement, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import io from "socket.io-client";

import {
  TSocketData,
  TAccBalanceData,
  TCurrency,
  TDeposit,
} from "../../types/types";
import { QueryKeys } from "../../enums/react-query";
import { getAvailableBalances } from "../../services/dashboard/dashboard.service";
import classNames from "classnames";
import { IoCopyOutline } from "react-icons/io5";
import { useAppStore } from "../../store/store";
import { toast } from "sonner";
import { currencyFormatter } from "../../utils/helper";
import CopyToClipboard from "react-copy-to-clipboard";

const socket = io(process.env.REACT_APP_SOCKET_API);

type Prop = {
  currencies: TCurrency[] | undefined;
};

export const UsdcDepositForm = ({ currencies }: Prop): ReactElement => {
  const { userData } = useAppStore();

  const { data: accBalData } = useQuery({
    queryKey: [QueryKeys.GETAVAILABLEBALANCES],
    queryFn: async () => {
      const res = await getAvailableBalances();
      return res.data;
    },
  });

  const usdcAccount: TAccBalanceData | undefined = accBalData?.payload?.find(
    (acc: TAccBalanceData) => {
      return acc.currencySymbol === "($)";
    },
  );

  useEffect(() => {
    socket.on("payment_status", (data: TSocketData) => {
      if (data?.distributor !== userData._id) return;
      if (data?.status === "failed") {
        toast.error(
          `${data.message}. Your deposit has been refunded to your wallet.`,
        );
      } else if (data?.status === "success") {
        const deposit = data?.data as TDeposit;
        const currency = deposit.currency;
        toast.success(
          `${data.message}. ${currency.symbol}${currencyFormatter(+deposit.amount)} has been credited to your ${currency.name} balance.`,
        );
      }
    });

    return () => {
      socket.off("payment_status");
    };
  }, []);

  return (
    <>
      <Form.Item
        name="chain"
        label="Chain"
        rules={[
          {
            required: true,
            message: "Please input a chain!",
          },
        ]}
      >
        <Input
          className="Nunito h-[44px] !py-[12px] !px-[16px] bg-[#F9FAFB] border-[1px] !border-[#D1D5DB] focus:!shadow-[0_0px_0px_1px_#ffa30094] rounded-[8px] text-[16px] font-[300] !text-[#667085]"
          readOnly
        />
      </Form.Item>
      <div className="relative">
        <CopyToClipboard
          onCopy={() => {
            toast.success("Copied to clipboard!");
          }}
          text={process.env.REACT_APP_EAZIPOWER_DEPOSIT_WALLET!}
        >
          <IoCopyOutline className="absolute z-10 text-xl transition-transform duration-300 cursor-pointer top-11 text-brand-darkYellow right-3 hover:animate-pulse hover:scale-125" />
        </CopyToClipboard>
        <Form.Item
          name="walletAddress"
          label="Wallet Address"
          rules={[
            {
              required: true,
              message: "Please input wallet address!",
            },
          ]}
        >
          <Input
            className="Nunito h-[44px] !py-[12px] !px-[16px] bg-[#F9FAFB] border-[1px] !border-[#D1D5DB] focus:!shadow-[0_0px_0px_1px_#ffa30094] rounded-[8px] text-[16px] !text-[#667085] font-[500]"
            readOnly
          />
        </Form.Item>
      </div>
      <div className="flex items-center justify-end -mt-[22px]">
        <p
          className={classNames(
            { hidden: !usdcAccount },
            "text-brand-darkYellow text-[11px] font-[500] align-right",
          )}
        >
          Avail Bal: ($){currencyFormatter(usdcAccount?.availableBalance)}
        </p>
      </div>
      <div>
        <p className="text-[16px]">Payment</p>
        <div className="flex flex-wrap justify-between bg-[#fefbf5] p-3 rounded-md mt-3">
          <p className="font-[500]">Instructions</p>
          <div className="flex items-center gap-1">
            <p className="text-[14px] font-[500]">USDC</p>
            <img src={usdcAccount?.currencyIcon} className="w-4 h-4" />
          </div>
          <p className="w-full mt-3 text-gray-700 basis-full">
            Send your USDC deposit to the wallet address above with the
            following memo:{" "}
            <span className="ml-1 font-bold lowercase">
              {userData.fullName.split(" ")[0]}#{userData.paymentId}
            </span>
            <span className="relative">
              <CopyToClipboard
                onCopy={() => {
                  toast.success("Copied to clipboard!");
                }}
                text={`${userData.fullName.split(" ")[0]}#${userData.paymentId}`.toLowerCase()}
              >
                <IoCopyOutline className="absolute top-0 z-10 text-xl transition-transform duration-300 cursor-pointer -right-6 text-brand-darkYellow hover:animate-pulse hover:scale-125" />
              </CopyToClipboard>
            </span>
          </p>
        </div>
        <span className="text-[14px] font-semibold text-brand-darkYellow mt-4 block">
          Note: Include the provided memo exactly as shown. Incorrect memos
          result in a failed transfer. Deposits reflect in your account within a
          minute.
        </span>
      </div>
    </>
  );
};
